import { useMemo, type CSSProperties } from "react";
import { Typography } from "antd";
import { CloseCircleOutlined } from "../icons";
import type { DispatchDetectionReport } from "../../../shared/dispatchTypes";
import {
  CellSection,
  DispatchCoverageBadge,
  MessageContentView,
  RoomChipList,
  SenderList,
  formatTimeRange,
  isAggregatedReport,
  type AlignedRow,
  type TargetMatchItem,
} from "./dispatchBoardShared";

const { Text } = Typography;

function MissingTargetCell() {
  return (
    <div className="dispatch-board-empty-cell dispatch-board-empty-cell--target-missing">
      <CloseCircleOutlined className="dispatch-board-empty-cell__icon" />
      <span className="dispatch-board-empty-cell__title">未发单</span>
      <span className="dispatch-board-empty-cell__hint">源头/采集已有，发单群未命中</span>
    </div>
  );
}

function TargetGroupCell({
  target,
  onOpenRooms,
  onPreview,
}: {
  target: TargetMatchItem | null;
  onOpenRooms: (target: TargetMatchItem) => void;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  if (!target) {
    return <MissingTargetCell />;
  }

  const { item } = target;
  const matchedCount = item.targetHitRoomCount || 0;
  const totalCount = item.targetConfiguredRoomCount || target.roomCount;
  return (
    <div className="dispatch-board-cell dispatch-board-cell--target">
      <div className="dispatch-board-cell-top">
        <DispatchCoverageBadge matchedCount={matchedCount} totalCount={totalCount} onClick={() => onOpenRooms(target)} />
      </div>
      <CellSection label="发送人">
        <SenderList senders={item.senders} />
      </CellSection>
      <CellSection label="消息内容">
        {item.linkDistribution ? <div className="dispatch-board-link-banner">链接分布：{item.linkDistribution}</div> : null}
        <MessageContentView item={item} className="dispatch-board-content" onPreview={onPreview} />
      </CellSection>
      <div className="dispatch-board-footer">
        <span className="dispatch-board-stat">{item.targetMessageCount} 条</span>
        <span className="dispatch-board-time">{formatTimeRange(item.earliestTs, item.latestTs)}</span>
      </div>
    </div>
  );
}

/** 时间线视图：源头群 / 采集群 / 发单群分列排布 */
export function DispatchAlignedBoard({
  report,
  rows,
  onOpenRooms,
  onPreview,
}: {
  report: DispatchDetectionReport;
  rows: AlignedRow[];
  onOpenRooms: (target: TargetMatchItem) => void;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  const aggregated = isAggregatedReport(report);
  const targetGroups = useMemo(
    () => [...report.targetGroups].sort((a, b) => a.groupName.localeCompare(b.groupName, "zh-CN")),
    [report.targetGroups]
  );
  const hasSource = report.sourceRooms.length > 0;
  const hasCollect = report.collectRooms.length > 0;
  const targetColumnCount = Math.max(targetGroups.length, 1);
  const columnCount = (hasSource ? 1 : 0) + (hasCollect ? 1 : 0) + 1;

  return (
    <div className="dispatch-board" style={{ "--dispatch-board-columns": columnCount } as CSSProperties}>
      <div className="dispatch-board-header">
        {hasSource ? (
          <div className="dispatch-board-header-col dispatch-board-header-col--source">
            <span className="dispatch-board-column-title">源头群</span>
            <RoomChipList rooms={report.sourceRooms} />
          </div>
        ) : null}
        {hasCollect ? (
          <div className="dispatch-board-header-col dispatch-board-header-col--collect">
            <span className="dispatch-board-column-title">采集群</span>
            <RoomChipList rooms={report.collectRooms} />
          </div>
        ) : null}
        <div
          className="dispatch-board-header-targets"
          style={{ gridTemplateColumns: `repeat(${targetColumnCount}, minmax(0, 1fr))` }}
        >
          {targetGroups.length > 0 ? (
            targetGroups.map((group) => (
              <div key={group.groupId} className="dispatch-board-header-col dispatch-board-header-col--target">
                <span className="dispatch-board-column-title">发单群</span>
                {aggregated ? (
                  <span
                    className="dispatch-board-unit-name"
                    title={report.businessUnits.map((unit) => unit.name).join("、")}
                  >
                    {report.businessUnits.length} 个业务单元合并
                  </span>
                ) : (
                  <span className="dispatch-board-group-name" title={group.groupName}>
                    {group.groupName}
                  </span>
                )}
              </div>
            ))
          ) : (
            <div className="dispatch-board-header-col dispatch-board-header-col--target">
              <span className="dispatch-board-column-title">发单群</span>
              <Text type="secondary">暂无发单群配置</Text>
            </div>
          )}
        </div>
      </div>

      <div className="dispatch-board-body">
        {rows.map((row, index) => (
          <div key={row.key} className="dispatch-board-row" data-row-index={index % 2}>
            {hasSource ? (
              <div className="dispatch-board-col dispatch-board-col--source">
                {row.source ? (
                  <div className="dispatch-board-cell dispatch-board-cell--source">
                    <CellSection label="发送人">
                      <SenderList senders={row.source.senders} />
                    </CellSection>
                    <CellSection label="消息内容">
                      <MessageContentView item={row.source} className="dispatch-board-content" onPreview={onPreview} />
                    </CellSection>
                    <div className="dispatch-board-footer">
                      <span className="dispatch-board-time">
                        {formatTimeRange(row.source.earliestTs, row.source.latestTs)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="dispatch-board-empty-cell">未命中源头群</div>
                )}
              </div>
            ) : null}
            {hasCollect ? (
              <div className="dispatch-board-col dispatch-board-col--collect">
                {row.collect ? (
                  <div className="dispatch-board-cell dispatch-board-cell--collect">
                    <CellSection label="发送人">
                      <SenderList senders={row.collect.senders} />
                    </CellSection>
                    <CellSection label="消息内容">
                      <MessageContentView item={row.collect} className="dispatch-board-content" onPreview={onPreview} />
                    </CellSection>
                    <div className="dispatch-board-footer">
                      <span className="dispatch-board-stat">{row.collect.roomCount} 个群</span>
                      <span className="dispatch-board-time">
                        {formatTimeRange(row.collect.earliestTs, row.collect.latestTs)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="dispatch-board-empty-cell">未命中采集群</div>
                )}
              </div>
            ) : null}
            <div
              className="dispatch-board-target-cols"
              style={{ gridTemplateColumns: `repeat(${targetColumnCount}, minmax(0, 1fr))` }}
            >
              {targetGroups.length > 0 ? (
                targetGroups.map((group) => {
                  const target = row.targets.find((item) => item.groupId === group.groupId) || null;
                  return (
                    <div key={group.groupId} className="dispatch-board-col dispatch-board-col--target">
                      <TargetGroupCell target={target} onOpenRooms={onOpenRooms} onPreview={onPreview} />
                    </div>
                  );
                })
              ) : (
                <div className="dispatch-board-col dispatch-board-col--target">
                  <div className="dispatch-board-empty-cell">暂无发单群配置</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
